import { Save, WarningRounded } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

interface UnsavedChangesDialogProps {
  open: boolean;
  isSaving: boolean;
  onStay: () => void;
  onDiscard: () => void;
  onSave: () => void;
}

export function UnsavedChangesDialog({
  open,
  isSaving,
  onStay,
  onDiscard,
  onSave,
}: UnsavedChangesDialogProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onStay}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`,
          border: 1,
          borderColor: alpha(theme.palette.divider, 0.1),
        },
      }}
    >
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
          }}
        >
          <WarningRounded sx={{ fontSize: 32, color: 'warning.main' }} />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          {t('editor.unsaved_changes_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {t('editor.unsaved_changes_description')}
        </Typography>

        <Stack direction="column" spacing={1.5} sx={{ width: '100%' }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<Save fontSize="small" />}
            onClick={onSave}
            disabled={isSaving}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              py: 1,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
            }}
          >
            {isSaving ? t('editor.saving') : t('editor.unsaved_changes_save_and_leave')}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={onDiscard}
            disabled={isSaving}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              py: 1,
            }}
          >
            {t('editor.unsaved_changes_discard_and_leave')}
          </Button>

          <Button
            fullWidth
            variant="text"
            color="inherit"
            onClick={onStay}
            disabled={isSaving}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              py: 1,
              color: 'text.secondary',
            }}
          >
            {t('editor.unsaved_changes_stay')}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}
